/* eslint-disable no-console */
import { AppKind, TellentClientBootstrapDataV2 } from '@core/tellent-types';
import {
  parametrizedRoutePath,
  tellentHomeRouteFactory,
  tellentHomeSettingsRouteFactory,
  tellentRouteFactory,
} from '@core/tellent-util';

import { TellentClient } from '../app/tellent-client';

export function setupClient(
  client: TellentClient,
  data: Partial<TellentClientBootstrapDataV2> | null | void,
  appKind: `${AppKind}`,
) {
  if (!data) throw new Error('Bootstrap failed');

  console.log('onClientAuthorized', data);

  const { features, user, member, ...rest } = data;

  const seats = member?.seats.map(s => s.app.kind);
  const availableOrgs = user?.members.map(m =>
    [m.org.guid, m.seats.map(s => s.app.kind).join(', '), m.org.name].join(' | '),
  );

  const debug = { features, ...rest, seats, availableOrgs };

  document.getElementById('debug')!.innerText = JSON.stringify(debug, null, 2);

  client.navigation.then(nav => {
    nav.location.set(location.hash);
    nav.recognizedRoutes.set([
      tellentHomeRouteFactory('/#/home'),
      ...buildRecognizedRoutes(data?.features?.oneSettings!, appKind),
    ]);

    document.getElementById('open-sidebar')?.addEventListener('click', () => {
      nav.minimized.set(false);
      nav.forceMaximized.set(true);
    });

    nav.registerLinkHandler(url => {
      console.log(url);

      if (url.startsWith('http')) {
        window.open(url);
      } else {
        // const href = `${window.location.origin}${window.location.pathname}${url}`;
        // location.href = href;
        location.hash = url;
        nav.location.set(url);
      }
    });

    window.addEventListener('popstate', () => nav.location.set(location.hash));
  });
}

const buildRecognizedRoutes = (oneSettingsEnabled: boolean, appKind: `${AppKind}`) => {
  const buildRoutes = (routes: Record<string, string>) =>
    Object.entries(routes).map(([key, value]) =>
      tellentRouteFactory(key, value.includes('/:') ? parametrizedRoutePath(value) : value),
    );

  const recognizedRoutes = [
    tellentHomeRouteFactory('#/home'),
    ...buildRoutes(routesByAppKind[appKind].main),
  ];

  if (oneSettingsEnabled) {
    recognizedRoutes.push(
      tellentHomeSettingsRouteFactory('#/settings/profile'),
      ...buildRoutes(routesByAppKind[appKind].settings),
    );
  }

  return recognizedRoutes;
};

const routesByAppKind: Record<
  `${AppKind}`,
  { main: Record<string, string>; settings: Record<string, string> }
> = {
  admin: {
    main: {
      home: '#/home',
      dashboard: '#/dashboard',
      profile: '#/profile/:userId',
      team_members: '#/team_members',
      company: '#/company',
    },
    settings: {},
  },
  recruitment: {
    main: {
      dashboard: '#/dashboard',
      profile: '#/profile/:userId',
      home: '#/home',
      candidates: '#/candidates',
      requisitions: '#/requisitions',
      jobs: '#/jobs',
      mailbox: '#/mailbox',
      analytics: '#/analytics',
      talent_pools: '#/talent_pools',
      acquisition: '#/acquisition',
      marketplace: '#/marketplace',
    },
    settings: {
      'settings:profile': '#/settings/profile',
      'settings:notifications': '#/settings/notifications',
      'settings:company_settings': '#/settings/company_settings',
      'settings:team_members': '#/settings/team_members',
      'settings:hiring_roles': '#/settings/hiring_roles',
      'settings:billing': '#/settings/billing',
      'settings:locations': '#/settings/locations',
      'settings:referrals_hub': '#/settings/referrals_hub',
      'settings:meeting_rooms': '#/settings/meeting_rooms',
      'settings:shared_mailboxes': '#/settings/shared_mailboxes',
      'settings:audit_logs': '#/settings/audit_logs',
      'settings:texting': '#/settings/texting',
      'settings:requisition_approvals': '#/settings/requisition_approvals',
      'settings:unbiased_hiring': '#/settings/unbiased_hiring',
      'settings:custom_fields': '#/settings/custom_fields',
      'settings:disqualify_reasons': '#/settings/disqualify_reasons',
      'settings:tags_and_sources': '#/settings/tags_and_sources',
      'settings:departments': '#/settings/departments',
      'settings:public_links_to_candidates': '#/settings/public_links_to_candidates',
      'settings:event_scheduler': '#/settings/event_scheduler',
      'settings:job_templates': '#/settings/job_templates',
      'settings:pipeline_templates': '#/settings/pipeline_templates',
      'settings:candidate_profile_field_templates': '#/settings/candidate_profile_field_templates',
      'settings:email_templates': '#/settings/email_templates',
      'settings:evaluation_form_templates': '#/settings/evaluation_form_templates',
      'settings:questionnaire_templates': '#/settings/questionnaire_templates',
      'settings:screening_question_templates': '#/settings/screening_question_templates',
      'settings:referral_question_templates': '#/settings/referral_question_templates',
      'settings:jobs_widget': '#/settings/jobs_widget',
      'settings:mobile_apps': '#/settings/mobile_apps',
      'settings:sourcing_extension': '#/settings/sourcing_extension',
      'settings:webhooks': '#/settings/webhooks',
      'settings:personal_api_tokens': '#/settings/personal_api_tokens',
      'settings:import': '#/settings/import',
    },
  },
  core_hr: {
    main: {
      dashboard: '#/dashboard',
      profile: '#/profile/:userId',
      'people:list': '#/people/list',
      'people:organization_chart': '#/people/organization_chart',
      collection: '#/collection/:collectionId',
      'journeys:my_tasks': '#/journeys/my_tasks',
      'company:details': '#/company/details',
    },
    settings: {
      'settings:access_levels': '#/settings/access_levels',
      'settings:user_roles': '#/settings/user_roles',
      'settings:teams': '#/settings/teams',
      'settings:locations': '#/settings/locations',
      'settings:positions': '#/settings/positions',
      'settings:approval_flows': '#/settings/approval_flows',
      'settings:company_custom_modules': '#/settings/company_custom_modules',
      'settings:company_documents': '#/settings/company_documents',
      'settings:whistleblowing': '#/settings/whistleblowing',
      'settings:employee_custom_fields': '#/settings/employee_custom_fields',
      'settings:employee_custom_modules': '#/settings/employee_custom_modules',
      'settings:employee_documents': '#/settings/employee_documents',
      'settings:journey_types': '#/settings/journey_types',
      'settings:attendance_work_schedules': '#/settings/attendance_work_schedules',
      'settings:attendance_projects': '#/settings/attendance_projects',
      'settings:attendance_clock_in_station': '#/settings/attendance_clock_in_station',
      'settings:time_off_policies': '#/settings/time_off_policies',
      'settings:time_off_types': '#/settings/time_off_types',
      'settings:payroll_types': '#/settings/payroll_types',
      'settings:expense_categories': '#/settings/expense_categories',
      'settings:integrations_apps': '#/settings/integrations_apps',
      'settings:integrations_api_keys': '#/settings/integrations_api_keys',
    },
  },
  grow: {
    main: {
      dashboard: '#/dashboard',
      profile: '#/profile/:userId',
      overview: '#/overview',
      trainings: '#/trainings',
      goals: '#/goals',
      users: '#/users',
    },
    settings: {
      'settings:notifications': '#/settings/notifications',
      'settings:company': '#/settings/company',
      'settings:custom_fields': '#/settings/custom_fields',
      'settings:groups': '#/settings/groups',
      'settings:trainings': '#/settings/trainings',
    },
  },
};
